import { useRef } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { selectDisableEditorState } from '@/store/slices/editor/selector'
import { customAmplitude } from '@/utils/customAmplitude'
import { useAppDispatch } from '@/store/store'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import { selectUserIsPremium } from '@/store/slices/user/selectors'
import DownloadPopover from '../FloatingInspectorButton/SharedComponents/Popover/DownloadPopover/DownloadPopover'
import WithPopover, { WithPopoverHandle } from '../FloatingInspectorButton/WithPopover'
import DefaultButton from '@/components/DefaultButton'
import Download from '../Icons/Download'
import { lightTheme } from '@/customTheme'
import { FormatOption } from './components/downloadTypes'

interface PremiumPromptConfig {
  value: string
  openOnDownloadAllButton?: boolean
  openOnDownloadPopoverDownloadButton?: boolean
}

interface DownloadAllProps {
  /** When provided, these images are downloaded instead of the canvas scenes. */
  downloadScope?: () => string[] | Promise<string[]>
  /** When provided, replaces the default save step; receives the chosen format and quality multiplier. */
  onSave?: (format: FormatOption, quality: number) => Promise<void>
  destination: string
  source: string
  disabled?: boolean
  /** Show the Photo Format dropdown. Default: true */
  hasImages?: boolean
  /** Show the disabled Video Format (MP4) dropdown. Default: false */
  hasVideos?: boolean
  /** Initial format key for the popover when no editor is present to auto-detect transparency.
   *  Pass 'png' when the batch contains transparent images so the default is not lossy. */
  defaultFormatKey?: string
  /** Override the button label. Defaults to 'Download all'. */
  label?: string
  /** Show the "Remove watermark" button in the popover. Default: true */
  showWatermarkButton?: boolean
  /** Show a spinner on the Download button while the export is in progress. Default: false */
  showDownloadLoader?: boolean
  /** When provided, free users see a premium prompt based on the configured trigger points. */
  premiumPrompt?: PremiumPromptConfig
  /** Return false to handle the action without opening download settings. */
  onBeforeOpen?: () => boolean
  /** Preserve the action and accessible name while hiding its label in compact headers. */
  iconOnly?: boolean
}

function DownloadAll({
  downloadScope,
  onSave,
  destination,
  source,
  disabled,
  hasImages,
  hasVideos,
  defaultFormatKey,
  label,
  showWatermarkButton,
  showDownloadLoader,
  premiumPrompt,
  onBeforeOpen,
  iconOnly = false,
}: DownloadAllProps) {
  const dispatch = useAppDispatch()
  const disableEditorState = useSelector(selectDisableEditorState)
  const hasPremium = useSelector(selectUserIsPremium)
  const { t } = useTranslation()
  const popoverRef = useRef<WithPopoverHandle>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const isDisabled = disableEditorState.disableNavbar || disabled
  const isPhotoshootDownloadAll = destination === 'Photoshoot' || source === 'Photoshoot'
  const buttonLabel = label ?? t('Download all')

  return (
    <WithPopover
      ref={popoverRef}
      popoverContent={(_buttonRef, close) => (
        <DownloadPopover
          buttonRef={anchorRef as React.RefObject<HTMLElement>}
          onClose={close}
          downloadScope={downloadScope ?? 'all'}
          destination={destination}
          source={source}
          onSave={onSave}
          hasImages={hasImages}
          hasVideos={hasVideos}
          defaultFormatKey={defaultFormatKey}
          showWatermarkButton={showWatermarkButton}
          showDownloadLoader={showDownloadLoader}
          downloadButtonOpenPremiumPrompt={
            premiumPrompt?.openOnDownloadPopoverDownloadButton ? premiumPrompt.value : undefined
          }
          placement="bottomRight"
        />
      )}
    >
      {({ isOpen }) => (
        <div ref={anchorRef} style={{ display: 'inline-flex' }}>
          <DefaultButton
            type="primary"
            icon={iconOnly ? 'iconOnly' : 'iconLeft'}
            iconComponent={<Download size={24} fill={lightTheme.colors.white} />}
            label={buttonLabel}
            ariaLabel={buttonLabel}
            tooltip={iconOnly ? buttonLabel : undefined}
            disabled={isDisabled}
            isActive={isOpen}
            onClick={() => {
              if (isDisabled) return
              if (onBeforeOpen && !onBeforeOpen()) return
              if (isPhotoshootDownloadAll && !hasPremium) {
                dispatch(setOpenModalTryPremium({ isOpen: true, source: 'BtSubscriptionPhotoshoot' }))
                customAmplitude('Premium Prompt', { Source: 'BtSubscriptionPhotoshoot', Type: 'Standard' })
                return
              }
              if (premiumPrompt?.openOnDownloadAllButton && !hasPremium) {
                dispatch(setOpenModalTryPremium({ isOpen: true, source: premiumPrompt.value }))
                customAmplitude('Premium Prompt', { Source: premiumPrompt.value, Type: 'Standard' })
                return
              }
              popoverRef.current?.togglePopover()
            }}
          />
        </div>
      )}
    </WithPopover>
  )
}

export default DownloadAll
